import { createElement } from 'react';
import { captureException } from '@sentry/react';
import { BiDuplicate } from 'react-icons/bi';
import { TbArchive, TbArchiveOff, TbLock, TbLockOpen, TbStarFilled, TbStarOff } from 'react-icons/tb';
import { StateCreator } from 'zustand';

import { queryClient } from '@/clients';
import { optimisticallyReconcileMemoAssociations } from '@/queries/tanstack/database/entities/resourceAssociations';
import {
    fetchPrefetchedDetailQuery,
    getPrefetchedDetailQueryData,
    workspaceResourceDetailQueryOptions,
    workspaceResourceQueryKeys,
} from '@/queries/tanstack/prefetch';
import { memoService } from '@/services';
import { toast } from '@/shared/components/toasts/PlatformToast';
import {
    appendEntity,
    createSingleFlightKey,
    mergeEntity,
    mergeEntityById,
    removeEntityById,
    replaceEntityById,
    replaceOptimisticEntityById,
    runSingleFlight,
    upsertEntityById,
} from '@/stores/helpers/collections';
import { reduceCachedEntityDelta, reduceEntityDelta } from '@/stores/helpers/delta';
import { Memo } from '@/types';

import { MemoActions, MemoState } from '../types';

export const createMemoActions: StateCreator<MemoState & MemoActions, [['zustand/immer', never]], [], MemoActions> = (
    set,
    get,
) => ({
    // Core
    fetchMemos: async (params) => {
        set({ loading: true });

        try {
            const memos = await memoService.list(params);

            set({ memos, loading: false, error: undefined });
        } catch (error) {
            captureException(error);

            set({ loading: false, error: 'Failed to load memos' });
        }
    },
    fetchMemoById: async (id) => {
        const queryOptions = workspaceResourceDetailQueryOptions.memos(id);
        const cachedMemo = getPrefetchedDetailQueryData(queryClient, queryOptions);

        set({
            loadingMemo: !cachedMemo,
            error: undefined,
            selectedMemo: cachedMemo ?? (get().selectedMemo?.id === id ? get().selectedMemo : null),
        });

        try {
            const memo = await fetchPrefetchedDetailQuery(queryClient, queryOptions);

            set({ selectedMemo: memo, loadingMemo: false });
        } catch (error) {
            captureException(error);

            set((state) => {
                state.error = 'Failed to fetch memo detail';
                state.loadingMemo = false;
            });
        }
    },
    clearSelectedMemo: () => {
        set((state) => {
            state.selectedMemo = null;
        });
    },
    createMemo: async (input) =>
        runSingleFlight(createSingleFlightKey('memos:create', input), async () => {
            const tempId = `temp-${Date.now()}`;
            const optimistic = { id: tempId, ...input } as Memo;

            set({ memos: appendEntity(get().memos, optimistic) });

            try {
                const memo = await memoService.create(input);

                set({ memos: replaceOptimisticEntityById(get().memos, tempId, memo) });

                return memo;
            } catch (error) {
                captureException(error);

                set({ memos: removeEntityById(get().memos, tempId), error: 'Failed to create memo' });
            }
        }),
    updateMemo: async (id, updates, options) => {
        const prev =
            get().memos.find((f) => f.id === id) ?? (get().selectedMemo?.id === id ? get().selectedMemo : null);
        if (!prev) {
            if (options?.throwOnError) throw new Error('Memo is no longer available');
            return;
        }

        const associationMutation =
            'body' in updates
                ? optimisticallyReconcileMemoAssociations({
                      body: updates.body,
                      previousBody: prev.body,
                      queryClient,
                      source: {
                          id: prev.id,
                          label: prev.title || 'Untitled Memo',
                          resourceType: 'memos',
                      },
                      workspaceId: prev.workspace?.id,
                  })
                : undefined;

        set({
            memos: mergeEntityById(get().memos, id, updates),
            selectedMemo:
                get().selectedMemo?.id === id ? mergeEntity(get().selectedMemo!, updates) : get().selectedMemo,
        });
        queryClient.setQueryData(workspaceResourceQueryKeys.memos(id), (existingMemo: Memo | undefined) =>
            mergeEntity(existingMemo ?? prev, updates),
        );

        try {
            const memo = await memoService.update(id, updates);
            const currentMemo =
                get().selectedMemo?.id === id
                    ? get().selectedMemo
                    : get().memos.find((existingMemo) => existingMemo.id === id);
            const currentBody = currentMemo?.body;
            const updatedBody = 'body' in updates ? updates.body : undefined;
            const shouldPreserveLocalBody =
                typeof currentBody === 'string' &&
                memo.body !== currentBody &&
                (updatedBody === undefined || currentBody !== updatedBody);
            const nextMemo = shouldPreserveLocalBody ? { ...memo, body: currentBody } : memo;

            queryClient.setQueryData(workspaceResourceQueryKeys.memos(id), nextMemo);

            set({
                memos: mergeEntityById(get().memos, id, nextMemo),
                selectedMemo:
                    get().selectedMemo?.id === id ? mergeEntity(get().selectedMemo!, nextMemo) : get().selectedMemo,
            });
            associationMutation?.commit();
        } catch (error) {
            if (!options?.throwOnError) captureException(error);
            associationMutation?.rollback();
            queryClient.setQueryData(workspaceResourceQueryKeys.memos(id), prev);

            set({
                memos: replaceEntityById(get().memos, id, prev),
                selectedMemo: get().selectedMemo?.id === id ? prev : get().selectedMemo,
                error: 'Failed to update memo',
            });
            if (options?.throwOnError) throw error;
        }
    },
    deleteMemo: async (id) => {
        const prev = get().memos;

        set({
            memos: removeEntityById(get().memos, id),
            selectedMemo: get().selectedMemo?.id === id ? null : get().selectedMemo,
        });

        try {
            await memoService.delete(id);
        } catch (error) {
            captureException(error);

            set({ memos: prev, error: 'Failed to delete memo' });
        }
    },
    applyMemoDelta: (packet) => {
        if (packet.entity !== 'memo') {
            return;
        }

        const next = reduceEntityDelta<Memo>(get().memos, get().selectedMemo, packet);

        if (!next) {
            return;
        }

        if (packet.operation === 'delete') {
            queryClient.removeQueries({ queryKey: workspaceResourceQueryKeys.memos(packet.entityId) });
        } else if (packet.payload) {
            queryClient.setQueryData(
                workspaceResourceQueryKeys.memos(packet.entityId),
                (existingMemo: Memo | undefined) => reduceCachedEntityDelta(existingMemo, packet),
            );
        }

        set({
            memos: next.items,
            selectedMemo: next.selected,
        });
    },
    // - Aliases
    duplicateMemo: async (id) => {
        const prev = get().memos;

        if (!prev) {
            return;
        }

        try {
            const duplicated = await memoService.duplicate(id);

            set({ memos: upsertEntityById(get().memos, duplicated, { prepend: true }) });

            toast(`Memo Duplicated`, {
                icon: createElement(BiDuplicate, { size: '1rem' }),
                description: duplicated.title,
                resource: { id: duplicated.id, type: 'memo' },
            });

            return duplicated;
        } catch (error) {
            captureException(error);

            set({ memos: prev, error: 'Failed to duplicate memo' });

            toast.error('Failed to duplicate memo');
        }
    },
    toggleMemoFavorite: async (id: string, favorited: boolean) => {
        const prev = get().memos.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set((state) => {
            const memo = state.memos.find((f) => f.id === id);
            if (memo) {
                memo.favorited = favorited;
            }

            // Also update selectedMemo if it matches
            if (state.selectedMemo?.id === id) {
                state.selectedMemo.favorited = favorited;
            }
        });

        try {
            await memoService.setFavorite(id, favorited);

            toast(`${favorited ? 'Added to' : 'Removed from'} Favorites`, {
                icon: createElement(favorited ? TbStarFilled : TbStarOff, { size: '1rem' }),
                description: prev.title,
                resource: { id, type: 'memo' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const memo = state.memos.find((f) => f.id === id);
                if (memo) {
                    memo.favorited = prev.favorited;
                }

                // Revert selectedMemo favorited if it matches
                if (state.selectedMemo?.id === id) {
                    state.selectedMemo.favorited = prev.favorited;
                }

                state.error = 'Failed to update favorite';
            });
        }
    },
    toggleMemoLock: async (id, locked) => {
        const prev = get().memos.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set((state) => {
            const memo = state.memos.find((f) => f.id === id);
            if (memo) {
                memo.locked = locked;
            }

            // Also update selectedMemo if it matches
            if (state.selectedMemo?.id === id) {
                state.selectedMemo.locked = locked;
            }
        });

        try {
            await memoService.setLocked(id, locked);

            toast(`Memo ${locked ? 'Locked' : 'Unlocked'}`, {
                icon: createElement(locked ? TbLock : TbLockOpen, { size: '1rem' }),
                description: prev.title,
                resource: { id, type: 'memo' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const memo = state.memos.find((f) => f.id === id);
                if (memo) {
                    memo.locked = prev.locked;
                }

                // Revert selectedMemo locked if it matches
                if (state.selectedMemo?.id === id) {
                    state.selectedMemo.locked = prev.locked;
                }

                state.error = 'Failed to update favorite';
            });
        }
    },
    toggleMemoArchive: async (id, archived) => {
        const prev = get().memos.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set((state) => {
            const memo = state.memos.find((f) => f.id === id);
            if (memo) {
                memo.archived = archived;
            }

            // Also update selectedMemo if it matches
            if (state.selectedMemo?.id === id) {
                state.selectedMemo.archived = archived;
            }
        });

        try {
            await memoService.setArchived(id, archived);

            toast(`Memo ${archived ? 'Archived' : 'Unarchived'}`, {
                icon: createElement(archived ? TbArchive : TbArchiveOff, { size: '1rem' }),
                description: prev.title,
                resource: { id, type: 'memo' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const memo = state.memos.find((f) => f.id === id);
                if (memo) {
                    memo.archived = prev.archived;
                }

                // Revert selectedMemo archived if it matches
                if (state.selectedMemo?.id === id) {
                    state.selectedMemo.archived = prev.archived;
                }

                state.error = 'Failed to update favorite';
            });
        }
    },
});
